import React, { useLayoutEffect, useRef } from "react"
import { Calendar, MapPin, User } from "lucide-react"
import FishTagBadgeGroup from "../box-labels/FishTagBadgeGroup.jsx"
import useInfiniteScrollTrigger from "../hooks/useInfiniteScrollTrigger.js"
import useWindowResizeTick from "../hooks/useWindowResizeTick.js"
import OrderStatusBadge from "./OrderStatusBadge.jsx"
import "./orders.css"

function OrderMobileRow({
    order,
    index,
    isChecked,
    isSelectable,
    onSelectedOrdersChange,
    selectedOrders,
    orderKey,
    getOrderKey,
    onOrderClick,
    resizeTick,
}) {
    const tripCode = String(order?.tripCode ?? order?.trip_code ?? "").trim()
    const fishTag = order?.fishTag ?? order?.fish_tag ?? ""
    const hasAside = Boolean(tripCode || String(fishTag ?? "").trim())

    const locationFull = String(order?.location ?? "").trim()
    const locationShort = String(order?.locationShort ?? order?.location ?? "").trim()
    const shipTo = String(order?.shipTo ?? "").trim()

    const showTripPrefixRef = useRef(true)
    const useFullLocationRef = useRef(false)

    const tripFitRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const tripMeasureRef = useRef(/** @type {HTMLSpanElement | null} */(null))
    const tripPrefixRef = useRef(/** @type {HTMLSpanElement | null} */(null))

    const locationLineRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const locationIconRef = useRef(/** @type {HTMLSpanElement | null} */(null))
    const locationMeasureFullRef = useRef(/** @type {HTMLSpanElement | null} */(null))
    const locationFullVisibleRef = useRef(/** @type {HTMLSpanElement | null} */(null))
    const locationShortVisibleRef = useRef(/** @type {HTMLSpanElement | null} */(null))

    useLayoutEffect(() => {
        // Trip prefix decision — DOM toggle only (no setState).
        if (tripCode && tripFitRef.current && tripMeasureRef.current && tripPrefixRef.current) {
            const available = Math.round(tripFitRef.current.getBoundingClientRect().width)
            const needed = tripMeasureRef.current.offsetWidth
            if (available > 0) {
                const prev = showTripPrefixRef.current
                const next = prev ? (needed <= available + 8) : (needed <= available + 1)
                if (next !== prev) {
                    showTripPrefixRef.current = next
                    tripPrefixRef.current.classList.toggle("v2-orders-fit-hidden", !next)
                }
            }
        } else if (tripPrefixRef.current) {
            showTripPrefixRef.current = true
            tripPrefixRef.current.classList.remove("v2-orders-fit-hidden")
        }

        // Location long vs short — measure stable line width (icon + text), toggle visibility only.
        if (
            locationLineRef.current &&
            locationMeasureFullRef.current &&
            locationFullVisibleRef.current &&
            locationShortVisibleRef.current
        ) {
            const lineWidth = Math.round(locationLineRef.current.getBoundingClientRect().width)
            const iconWidth = locationIconRef.current
                ? Math.round(locationIconRef.current.getBoundingClientRect().width)
                : 0
            const available = lineWidth - iconWidth - 6
            const fullNeeded = locationMeasureFullRef.current.offsetWidth

            if (available > 0) {
                const prev = useFullLocationRef.current
                const next = prev ? (fullNeeded <= available + 12) : (fullNeeded <= available - 2)
                if (next !== prev) {
                    useFullLocationRef.current = next
                    locationFullVisibleRef.current.classList.toggle("v2-orders-fit-hidden", !next)
                    locationShortVisibleRef.current.classList.toggle("v2-orders-fit-hidden", next)
                }
            }
        } else if (locationFullVisibleRef.current && locationShortVisibleRef.current) {
            useFullLocationRef.current = false
            locationFullVisibleRef.current.classList.add("v2-orders-fit-hidden")
            locationShortVisibleRef.current.classList.remove("v2-orders-fit-hidden")
        }
    }, [resizeTick, tripCode, locationFull, locationShort, shipTo])

    return (
        <div
            key={index}
            className="v2-mobile-list-row bg-white p-3 border-bottom position-relative w-100"
            style={{
                cursor: "pointer",
                backgroundColor: isChecked ? "rgba(15, 97, 112, 0.08)" : undefined,
            }}
            onClick={() => onOrderClick(order)}
        >
            <div
                className="v2-orders-mobile-header d-flex justify-content-between align-items-start gap-2 w-100"
            >
                <h3
                    className="fw-semibold mb-0 text-truncate min-w-0"
                    style={{ color: "#0f6170" }}
                >
                    {order.master}
                </h3>
                <div
                    className="d-flex align-items-center gap-2 flex-shrink-0"
                    onClick={(event) => event.stopPropagation()}
                >
                    <OrderStatusBadge
                        status={order.status}
                        className="v2-orders-mobile-status-badge"
                        title={order.status}
                    />
                    {isSelectable ? (
                        <input
                            type="checkbox"
                            className="form-check-input flex-shrink-0 m-0"
                            checked={Boolean(isChecked)}
                            onChange={(event) => {
                                const nextChecked = event.target.checked
                                const next = nextChecked
                                    ? [...selectedOrders, order]
                                    : selectedOrders.filter((o, i) => String(getOrderKey(o, i)) !== String(orderKey))
                                onSelectedOrdersChange(next)
                            }}
                            aria-label={`Select order ${order?.id ?? order?.master ?? ""}`}
                        />
                    ) : null}
                </div>
            </div>

            <div
                className={`v2-orders-mobile-row-body${hasAside ? "" : " v2-orders-mobile-row-body--solo"}`}
            >
                <div className="v2-orders-mobile-meta min-w-0">
                    <div className="v2-orders-mobile-meta-line text-secondary">
                        <User size={14} className="me-1 flex-shrink-0" />
                        <span className="text-truncate min-w-0">
                            {order.firstName} {order.lastName}
                        </span>
                    </div>

                    <div ref={locationLineRef} className="v2-orders-mobile-meta-line text-secondary">
                        <span ref={locationIconRef} className="me-1 flex-shrink-0 d-inline-flex">
                            <MapPin size={14} />
                        </span>
                        <div className="text-truncate min-w-0 w-100 v2-orders-measure-wrap">
                            <span
                                ref={locationMeasureFullRef}
                                className="v2-orders-measure-text"
                                aria-hidden="true"
                            >
                                {locationFull} • {shipTo}
                            </span>
                            <span ref={locationFullVisibleRef} className="v2-orders-fit-hidden">
                                {locationFull}
                            </span>
                            <span ref={locationShortVisibleRef}>
                                {locationShort}
                            </span>
                            {" • "}
                            {shipTo}
                        </div>
                    </div>

                    <div className="v2-orders-mobile-meta-line text-secondary">
                        <Calendar size={14} className="me-1 flex-shrink-0" />
                        <span className="text-truncate min-w-0">{order.orderDate}</span>
                    </div>

                    {order.sjNum ? (
                        <div className="v2-orders-mobile-meta-line text-secondary">
                            <span className="flex-shrink-0">SJ#:</span>
                            <span className="ms-1 fw-semibold text-truncate min-w-0">{order.sjNum}</span>
                        </div>
                    ) : null}
                </div>

                {hasAside ? (
                    <div className="v2-orders-mobile-aside">
                        {tripCode ? (
                            <div
                                ref={tripFitRef}
                                className="v2-orders-mobile-trip text-end text-truncate w-100 v2-orders-measure-wrap"
                                title={`Trip: ${tripCode}`}
                            >
                                <span
                                    ref={tripMeasureRef}
                                    className="v2-orders-measure-text"
                                    aria-hidden="true"
                                >
                                    Trip: {tripCode}
                                </span>
                                <span
                                    ref={tripPrefixRef}
                                >
                                    Trip:{" "}
                                </span>
                                {tripCode}
                            </div>
                        ) : null}
                        <div className="v2-orders-mobile-fish-tags">
                            <FishTagBadgeGroup
                                fishTag={fishTag}
                                collapseWhenOverflow
                                remeasureKey={resizeTick}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default function OrdersMobileList({
    orders,
    hasMore = false,
    isLoading = false,
    isLoadingInitial = false,
    onLoadMore = () => {},
    onOrderClick = (_order) => {},
    enableInfiniteScroll = true,
    selectedOrders = undefined,
    onSelectedOrdersChange = undefined,
}) {
    const sentinelRef = useInfiniteScrollTrigger({
        enabled: enableInfiniteScroll,
        hasMore,
        isLoading,
        onLoadMore,
    })
    const resizeTick = useWindowResizeTick()

    if (isLoadingInitial && orders.length === 0) {
        return (
            <div className="d-lg-none v2-mobile-list">
                <div className="v2-mobile-list-row bg-white rounded p-3 mb-3 text-secondary w-100">
                    Loading orders...
                </div>
            </div>
        )
    }

    return (
        <div className="d-lg-none v2-mobile-list">
            {orders.map((order, index) => {
                const getOrderKey = (o, i) => o?.id ?? o?.orderId ?? o?.master ?? i
                const orderKey = getOrderKey(order, index)
                const selected = Array.isArray(selectedOrders) ? selectedOrders : []
                const isSelectable = typeof onSelectedOrdersChange === "function"
                const isChecked = isSelectable && selected.some((o, i) => String(getOrderKey(o, i)) === String(orderKey))
                return (
                    <OrderMobileRow
                        key={index}
                        order={order}
                        index={index}
                        isChecked={isChecked}
                        isSelectable={isSelectable}
                        selectedOrders={selected}
                        onSelectedOrdersChange={onSelectedOrdersChange}
                        orderKey={orderKey}
                        getOrderKey={getOrderKey}
                        onOrderClick={onOrderClick}
                        resizeTick={resizeTick}
                    />
                )
            })}
            {enableInfiniteScroll ? (
                <>
                    <div ref={sentinelRef} style={{ height: "1px" }} />
                    {isLoading ? (
                        <div className="text-center text-secondary py-3"
                            style={{ fontSize: "0.875rem" }}>
                            Loading more...
                        </div>
                    ) : null}
                    {!hasMore && orders.length > 0 ? (
                        <div className="text-center text-secondary py-3"
                            style={{ fontSize: "0.875rem" }}>
                            End of orders
                        </div>
                    ) : null}
                </>
            ) : null}
        </div>
    )
}
